import React, { useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { compose } from 'redux';

// GLOBAL FUNCTIONS
import { controlName } from 'functions.js';

// HELPERS
import { dateProps } from './helpers/dateProps';
import { dateOptions } from './helpers/dateOptions';
import { handleSelect } from './helpers/handleSelect';
import { formikField } from '../../helpers/formik/formikField';
import { formCollapse } from '../../helpers/layout/formCollapse';
import { formGroup } from '../../helpers/layout/formGroup';
import { inputAppend } from '../../helpers/layout/inputAppend';
import { inputLabel } from '../../helpers/layout/inputLabel';
import { inputText } from '../../helpers/layout/inputText';
import { useHidden } from '../../helpers/layout/useHidden';
import { inputState } from '../../helpers/state/inputState';

// LOCAL VARIABLES
import { DATE_OPTIONS } from './helpers/dateOptions';

// LOCAL COMPONENTS
import DatePicker from './parts/DatePicker';
import TimePicker from './parts/TimePicker';
import RangePicker from './parts/RangePicker';
import Select from '../select/Select';
import Input from '../../parts/Input';
import Row from '../../../parts/Row';
import Group from '../../../parts/Group';

// STYLES
import styles from './date.module.scss';

// MAIN COMPONENT
const Date = compose(
  formikField,
  inputState,
  useHidden,
  formCollapse,
  formGroup,
  inputLabel,
  inputText,
  inputAppend,
  dateOptions,
  dateProps,
  handleSelect
)(
  ({
    className,
    name,
    startName,
    endName,
    option,
    selectOptions,
    onOptionSelect,
    onDateSelect,
    currentDate,
    currentTime,
    dateRange,
    startDate,
    endDate,
    minDate,
    maxDate,
    useNone, // preventing these from being passed to select
    useAll, // preventing these from being passed to select
    time,
    range,
    multiple,
    label = time ? 'Date/Time' : range ? 'Date Range' : multiple ? 'Date(s)' : 'Date',
    useDefault,
    useOptions,
    showPicker = !useOptions || option === 'custom',
    required,
    useApply,
    ...props
  }) => {
    // PROPS
    const { inline, vertical, columns, plaintext, fullWidth, Container = inline ? Group : Row } = props;

    // MEMOS
    const defaultSelectValue = useMemo(() => {
      if (!useOptions) return;
      if (!range && !currentDate) return;
      if (range && !startDate && !endDate) return;

      // Get first match
      return Object.keys(DATE_OPTIONS).find(key => {
        const o = DATE_OPTIONS[key];
        const dateValue = range ? [startDate, endDate].join('-') : currentDate;
        const optionValue = String(o.date || [o.startDate, o.endDate].join('-'));
        return optionValue === dateValue;
      }, '');
    }, [currentDate, startDate, endDate, range, useOptions]);

    // RENDER
    return plaintext ? (
      !!range ? (
        <Input value={startDate && endDate ? [startDate, endDate].join(' - ') : undefined} {...props} />
      ) : !!time ? (
        <Input value={currentDate && currentTime ? [currentDate, currentTime].join(' @ ') : undefined} {...props} />
      ) : (
        <Input value={currentDate.split(',').join(', ')} {...props} />
      )
    ) : (
      <Container
        className={clsx(styles.outer, inline ? styles.group : styles.row, fullWidth && styles.fullWidth, className)}
      >
        {useOptions && (
          <Select
            name={
              name || (startName && endName) ? controlName('option', 'dummy', name || startName + endName) : undefined
            }
            value={option}
            defaultValue={defaultSelectValue}
            placeholder={`Choose ${label}`}
            onSelect={onOptionSelect}
            options={selectOptions}
            formGroup={{
              className: clsx(
                styles.select,
                inline && styles.inline,
                vertical && styles.vertical,
                columns && styles.columns,
                fullWidth && styles.fullWidth
              )
            }}
            useDefault={useDefault}
            required={required}
            {...props}
          />
        )}
        {showPicker && (
          <>
            {!range ? (
              <>
                <DatePicker
                  name={
                    time
                      ? controlName('date', 'dummy', name)
                      : useOptions
                      ? controlName('picker', 'dummy', name)
                      : name
                      ? controlName(name, 'dummy')
                      : undefined
                  }
                  currentDate={currentDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  onSelect={onDateSelect}
                  onDateChange={onDateSelect}
                  formGroup={{
                    className: clsx(
                      styles.picker,
                      time && styles.timePicker,
                      inline && styles.inline,
                      vertical && styles.vertical,
                      columns && styles.columns,
                      fullWidth && styles.fullWidth
                    ),
                    cols: {
                      xs: time ? 12 : undefined
                    }
                  }}
                  multiple={multiple}
                  required={!!currentTime || !!required}
                  {...props}
                />
                {!!time && (
                  <TimePicker
                    name={name ? controlName('time', 'dummy', name) : undefined}
                    currentDate={currentDate}
                    value={currentTime}
                    minDate={minDate}
                    maxDate={maxDate}
                    formGroup={{
                      className: clsx(
                        styles.timePicker,
                        inline && styles.inline,
                        vertical && styles.vertical,
                        columns && styles.columns,
                        fullWidth && styles.fullWidth
                      ),
                      cols: {
                        xs: 12
                      }
                    }}
                    required={!!currentDate || !!required}
                    {...props}
                  />
                )}
              </>
            ) : (
              <RangePicker
                name={name}
                startName={startName}
                endName={endName}
                onDateSelect={onDateSelect}
                startDate={startDate}
                endDate={endDate}
                minDate={minDate}
                maxDate={maxDate}
                required={required}
                useApply={useApply}
                {...props}
              />
            )}
          </>
        )}
      </Container>
    );
  }
);

// CHILD COMPONENTS
Date.OPTIONS = DATE_OPTIONS;
Date.Time = props => <Date time {...props} />;
Date.Range = props => <Date range {...props} />;
Date.Multiple = props => <Date multiple {...props} />;

// EXPORT
export default Date;
